import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import AdminLayout from '../../components/AdminLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { 
  Plus, Youtube, Trash2, Search, Filter, Loader2, ExternalLink
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ManageYouTube = () => {
  const { getAuthHeader } = useAuth();
  const [youtubeLinks, setYoutubeLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    url: '',
    category: 'SSC CGL'
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCategory, setFilterCategory] = useState('All');

  const categories = ['All', 'SSC CGL', 'Bank PO', 'RBI Grade B', 'IBPS PO', 'SBI PO', 'SSC CHSL', 'IBPS Clerk'];

  useEffect(() => {
    fetchYouTubeLinks();
  }, []);

  const fetchYouTubeLinks = async () => {
    try {
      const response = await fetch(`${API_URL}/api/youtube`, {
        headers: getAuthHeader()
      });
      const data = await response.json();
      setYoutubeLinks(data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching YouTube links:', error);
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title || !formData.url) {
      alert('Please fill in all fields');
      return;
    }

    setSubmitting(true);

    try {
      const response = await fetch(`${API_URL}/api/youtube`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeader()
        },
        body: JSON.stringify({
          title: formData.title,
          url: formData.url,
          course_category: formData.category
        })
      });

      // Parse response safely
      const responseText = await response.text();
      let data;
      
      try {
        data = JSON.parse(responseText);
      } catch (parseError) {
        console.error('JSON Parse Error. Response:', responseText);
        throw new Error('Server returned invalid response');
      }

      if (!response.ok) {
        throw new Error(data.detail || 'Failed to add link');
      }

      alert('YouTube link added successfully!');
      
      // Reset form
      setFormData({ title: '', url: '', category: 'SSC CGL' });
      
      // Refresh list
      fetchYouTubeLinks();
    } catch (error) {
      console.error('Submit error:', error);
      alert(`Failed to add link: ${error.message}`);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id, title) => {
    if (!window.confirm(`Are you sure you want to delete "${title}"?`)) {
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/youtube/${id}`, {
        method: 'DELETE',
        headers: getAuthHeader()
      });

      if (!response.ok) {
        throw new Error('Delete failed');
      }

      alert('YouTube link deleted successfully!');
      fetchYouTubeLinks();
    } catch (error) {
      console.error('Delete error:', error);
      alert('Failed to delete link');
    }
  };

  const filteredLinks = youtubeLinks.filter(link => {
    const matchesSearch = link.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         link.url.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = filterCategory === 'All' || link.course_category === filterCategory;
    return matchesSearch && matchesCategory;
  });

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Manage YouTube Links</h1>
          <p className="text-gray-600 mt-2">Add and manage YouTube video links for courses</p>
        </div>

        {/* Add Link Form */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <Plus className="mr-2 text-red-600" size={24} />
              Add New YouTube Link
            </CardTitle>
            <CardDescription>Add YouTube video links for course content</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Video Title *
                  </label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData({...formData, title: e.target.value})}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-red-500"
                    placeholder="e.g., Data Interpretation Tricks"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Course Category *
                  </label>
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData({...formData, category: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-red-500"
                  >
                    {categories.filter(cat => cat !== 'All').map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  YouTube URL *
                </label>
                <input
                  type="url"
                  value={formData.url}
                  onChange={(e) => setFormData({...formData, url: e.target.value})}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-red-500"
                  placeholder="https://www.youtube.com/watch?v=..."
                />
              </div>

              <Button 
                type="submit" 
                disabled={submitting}
                className="w-full bg-red-600 hover:bg-red-700"
              >
                {submitting ? (
                  <>
                    <Loader2 className="mr-2 animate-spin" size={16} />
                    Adding...
                  </>
                ) : (
                  <>
                    <Plus className="mr-2" size={16} />
                    Add YouTube Link
                  </>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Links List */}
        <Card>
          <CardHeader>
            <CardTitle>YouTube Links</CardTitle>
          </CardHeader>
          <CardContent>
            {/* Search and Filter */}
            <div className="flex flex-col md:flex-row gap-4 mb-6">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                <input
                  type="text"
                  placeholder="Search links..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-red-500"
                />
              </div>
              <div className="relative">
                <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                <select
                  value={filterCategory}
                  onChange={(e) => setFilterCategory(e.target.value)}
                  className="pl-10 pr-8 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-red-500"
                >
                  {categories.map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>
            </div>

            {loading ? (
              <div className="text-center py-8">
                <Loader2 className="animate-spin mx-auto text-red-600" size={32} />
                <p className="text-gray-600 mt-2">Loading links...</p>
              </div>
            ) : filteredLinks.length === 0 ? (
              <div className="text-center py-12 bg-gray-50 rounded-lg">
                <Youtube className="mx-auto text-gray-400 mb-4" size={48} />
                <p className="text-gray-600">No YouTube links found</p>
                <p className="text-sm text-gray-500 mt-1">Add your first link to get started</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Title</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Category</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Uploaded By</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Date</th>
                      <th className="px-4 py-3 text-right text-sm font-medium text-gray-700">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {filteredLinks.map((link) => (
                      <tr key={link.id} className="hover:bg-gray-50">
                        <td className="px-4 py-4">
                          <div className="flex items-center">
                            <Youtube className="text-red-600 mr-3" size={20} />
                            <div>
                              <p className="font-medium text-gray-900">{link.title}</p>
                              <a 
                                href={link.url} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="text-xs text-red-600 hover:underline flex items-center"
                              >
                                View on YouTube <ExternalLink size={12} className="ml-1" />
                              </a>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-4 text-sm text-gray-700">
                          <span className="px-2 py-1 bg-red-100 text-red-800 rounded-full text-xs">
                            {link.course_category}
                          </span>
                        </td>
                        <td className="px-4 py-4 text-sm text-gray-700">{link.uploaded_by}</td>
                        <td className="px-4 py-4 text-sm text-gray-700">
                          {new Date(link.created_at).toLocaleDateString()}
                        </td>
                        <td className="px-4 py-4 text-right">
                          <Button
                            onClick={() => handleDelete(link.id, link.title)}
                            variant="destructive"
                            size="sm"
                          >
                            <Trash2 size={16} />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
};

export default ManageYouTube;
